import { useEffect, useState } from "react";
import { LayoutComponent } from "../LayoutComponent";
import {
  ActionButton,
  Cell,
  Column,
  Row,
  TableView,
  TableBody,
  TableHeader,
  Flex,
  Heading,
  Content,
  View,
  Link,
  ProgressCircle,
  DialogContainer,
  Dialog,
  DialogTrigger,
} from "@adobe/react-spectrum";
import StockChangeLineAddEdit from "./StockChangeLineAddEdit";
import { useDependency } from "../../../../contexts/DependencyProvider";
import { ListTransactionRequest } from "../../../../services/soap/invoice/requests/ListTransactionRequest";
import { useViewInfo } from "../../../../hooks/UseViewInfo";
import { useTranslation } from "react-i18next";
import { FPAData } from "../../../../infra/protected/FPA/FPAData";
import { ToastQueue } from '@adobe/react-spectrum';
import useComonentReload from "../../../../hooks/UseComponentReload";
import { formatDecimalToString } from "../../../../utils/helperUtil";
import { useDeviceDetection } from "../../../../hooks/UseDeviceDetection";
import { useTableViewHeight } from "../../../../utils/tableViewUtils";
import { AlertIconFill, TrashIcon } from "../../Icons/IconsLib";
import { DeleteEntryRequest } from "../../../../services/soap/finance/requests/DeleteEntryRequest";
import { ListJournalRequest } from "../../../../services/soap/finance/requests/ListJournalRequest";
import { GetNodeRequest } from "../../../../services/soap/tree/reqeusts/GetNodeRequest";
import styles from "./StockChangeLineComponent.module.css";
import { CreateEntryRequest } from "../../../../services/soap/finance/requests/CreateEntryRequest";
import { GetCodebookRequest } from "../../../../services/soap/item/requests/GetCodebookRequest";

/** Journal type for stock receipt entries in the Atollon finance system (type=10). */
const STOCK_JOURNAL_TYPE = "10";

export interface CodebookData {
  id: string;
  name: string;
  priority?: number;
}

export interface AttributeData {
  name?: string;
  type?: number;
  values?: string;
  codebooks?: CodebookData[];
}

interface StockChangeLineProps {
  selectedItem?: FPAData;
}

export interface RowData {
  id: string;
  itemQuantity: number;
  finance: string;
  itemName: string;
  itemRefId: string;
  transactionDate?: string;
  attrib0?: string;
  attrib0_id?: string;
  attrib1?: string;
  attrib1_id?: string;
  attrib2?: string;
  attrib2_id?: string;
  attrib3?: string;
  attrib3_id?: string;
  attrib4?: string;
  attrib4_id?: string;
}

export interface Journal {
  id: string;
  name: string;
  treeHandle?: string;
  templateAcc?: string;
  accountBase?: string;
  accountBaseName?: string;
  accountTotal?: string;
  accountTotalName?: string;
  field0Name?: string;
  field0Type?: string;
  field0Priority?: string;
  field0Values?: string;
  field1Name?: string;
  field1Type?: string;
  field1Priority?: string;
  field2Name?: string;
  field2Type?: string;
  field2Priority?: string;
  field3Name?: string;
  field3Type?: string;
  field3Priority?: string;
  field4Name?: string;
  field4Type?: string;
  field4Priority?: string;
  field5Type?: string;
  field5Priority?: string;
  field6Type?: string;
  field6Priority?: string;
  field7Type?: string;
  field7Priority?: string;
  field8Type?: string;
  field8Priority?: string;
  field8Values?: string;
  field9Type?: string;
  field9Priority?: string;
  field9Values?: string;
}

function StockChangeLine({ selectedItem }: Readonly<StockChangeLineProps>) {
  const { t } = useTranslation();
  let columnsData = [
    {
      name: t("item_name", {
        ns: "layout_components",
        defaultValue: "Item name",
      }),
      key: "itemName",
    },
    {
      name: t("item_qty", {
        ns: "layout_components",
        defaultValue: "Item qty",
      }),
      key: "itemQuantity",
      width: 100,
    },
    {
      name: t("amount", { ns: "layout_components", defaultValue: "Amount" }),
      key: "bookkeepingAmountWithCurr",
    },
    {
      name: t("date", { ns: "layout_components", defaultValue: "Date" }),
      key: "transactionDate",
    },
    {
      name: t("created_by", {
        ns: "layout_components",
        defaultValue: "Created by",
      }),
      key: "createdByName",
    },
    {
      name: t("journal", { ns: "layout_components", defaultValue: "Journal" }),
      key: "journalName",
    },
    {
      name: t("actions", { ns: "layout_components", defaultValue: "Actions" }),
      key: "id",
      width: 80,
    },
  ];

  let defaultRowData: RowData = {
    id: "0",
    itemQuantity: 1,
    finance: "0",
    itemName: "",
    itemRefId: "",
    attrib0: "",
    attrib0_id: "",
    attrib1: "",
    attrib1_id: "",
    attrib2: "",
    attrib2_id: "",
    attrib3: "",
    attrib3_id: "",
    attrib4: "",
    attrib4_id: "",
  };

  const [reloadComponent] = useComonentReload();
  const { store, invoiceService, financeService, treeService, itemService } =
    useDependency();
  const { isMobile } = useViewInfo();
  const deviceInfo = useDeviceDetection(isMobile);
  const [rowsData, setRowsData] = useState<any>([]);
  const [selectedRow, setSelectedRow] = useState<RowData>(defaultRowData);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [showAddEditRow, setShowAddEditRow] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<boolean>(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [defaultJournal, setDefaultJournal] = useState<Journal | null>(null);
  const [attributesCache, setAttributesCache] = useState<
    Record<string, AttributeData[]>
  >({});
  const [activeAttributesData, setActiveAttributesData] = useState<
    AttributeData[]
  >([]);

  // Use the utility for dynamic height calculations
  const { tableHeight, containerHeight, tableStyles } = useTableViewHeight(
    rowsData.length,
    {
      headerHeight: 50,
      rowHeight: 50,
      maxVisibleRows: 10,
      containerExtraSpace: 0, // Removed extra space to eliminate bottom gap
      maxContainerHeight: 550,
      showOverflow: false,
    },
  );

  let columnsDataMobile = [
    {
      name: t("item_name", {
        ns: "layout_components",
        defaultValue: "Item name",
      }),
      key: "itemName",
      width: 200,
    },
    {
      name: t("item_qty", {
        ns: "layout_components",
        defaultValue: "Item qty",
      }),
      key: "itemQuantity",
      width: 100,
    },
    {
      name: t("amount", { ns: "layout_components", defaultValue: "Amount" }),
      key: "bookkeepingAmount",
      width: deviceInfo.shouldUseMobileLayout() ? 200 : 150,
    },
    {
      name: t("date", { ns: "layout_components", defaultValue: "Date" }),
      key: "transactionDate",
      width: 120,
    },
    {
      name: t("created_by", {
        ns: "layout_components",
        defaultValue: "Created by",
      }),
      key: "createdByName",
      width: 150,
    },
    {
      name: t("journal", { ns: "layout_components", defaultValue: "Journal" }),
      key: "journalName",
      width: 150,
    },
    {
      name: t("actions", { ns: "layout_components", defaultValue: "Actions" }),
      key: "id",
      width: 80,
    },
  ];

  const loadAttributesFromJournal = async (
    journal: Journal,
  ): Promise<AttributeData[]> => {
    const fieldDefs = [
      {
        name: journal.field0Name,
        type: journal.field0Type,
        values: journal.field0Values,
      },
      { name: journal.field1Name, type: journal.field1Type, values: undefined },
      { name: journal.field2Name, type: journal.field2Type, values: undefined },
      { name: journal.field3Name, type: journal.field3Type, values: undefined },
      { name: journal.field4Name, type: journal.field4Type, values: undefined },
    ];
    return Promise.all(
      fieldDefs.map(async (f) => {
        const attr: AttributeData = {
          name: f.name,
          type: f.type ? Number(f.type) : undefined,
          values: f.values,
        };
        if (attr.type === 5 && attr.values) {
          try {
            const codebookRes: any = await itemService.getCodebook(
              new GetCodebookRequest(
                store.Server,
                store.SessionId,
                attr.values,
              ),
            );
            attr.codebooks = codebookRes.CODEBOOK[0].VALUES.map(
              (item: any) => ({
                id: item.id,
                name: item.name,
                priority: item.priority,
              }),
            );
          } catch {
            // ignore codebook load errors
          }
        }
        return attr;
      }),
    );
  };

  useEffect(() => {
    if (!defaultJournal) return;
    const cacheKey = defaultJournal.id;
    if (attributesCache[cacheKey]) {
      setActiveAttributesData(attributesCache[cacheKey]);
      return;
    }
    loadAttributesFromJournal(defaultJournal).then((attrs) => {
      setAttributesCache((prev) => ({ ...prev, [cacheKey]: attrs }));
      setActiveAttributesData(attrs);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultJournal]);

  useEffect(() => {
    const loadDefaultJournal = async () => {
      try {
        const response: any = await financeService.listJournal(
          new ListJournalRequest(store.Server, store.SessionId),
        );

        const items: any[] = (
          Array.isArray(response?.JOURNAL) ? response.JOURNAL : []
        ).filter((x: any) => String(x?.type ?? "") === STOCK_JOURNAL_TYPE);

        const preferred =
          items.find((x: any) => String(x?.isPrimary ?? "") === "1") ||
          items[0];
        if (!preferred?.id) return;
        console.log("preferred", preferred);
        // Step 1: resolve journal node id (= treeHandle for CreateEntry) and templateAclh
        const INVOICECODE =
          store.session?.organization.DEFAULTS?.DEFAULT.find(
            (item: any) => item.ident === "INVOICENODE",
          )?.node || "unknown_invoice_code";
        console.log("INVOICECODE", INVOICECODE);

        const journalNodeResp: any = await treeService.getNode(
          new GetNodeRequest(store.Server, store.SessionId, {
            treeHandle: INVOICECODE,
          }),
        );
        const journalNode: any = journalNodeResp?.NODE;
        console.log("journalNode", journalNode);
        // Step 2: resolve templateAclh → templateAcc
        let templateAcc: string | undefined;
        if (journalNode?.templateAclh) {
          const templateNodeResp: any = await treeService.getNode(
            new GetNodeRequest(store.Server, store.SessionId, {
              treeHandle: journalNode.id,
            }),
          );
          console.log("templateNodeResp", templateNodeResp);
          const id = templateNodeResp?.NODE?.id;
          if (id) templateAcc = templateNodeResp?.NODE?.aclh;
        }

        setDefaultJournal({
          id: String(preferred.id),
          name: preferred.name || "",
          treeHandle: journalNode?.id
            ? String(journalNode.id)
            : preferred.treeHandle,
          templateAcc,
          accountBase: preferred.accountBase
            ? String(preferred.accountBase)
            : undefined,
          accountBaseName: preferred.accountBaseName,
          accountTotal: preferred.accountTotal
            ? String(preferred.accountTotal)
            : undefined,
          accountTotalName: preferred.accountTotalName,
          field0Name: preferred.field0Name,
          field0Type: preferred.field0Type,
          field0Priority: preferred.field0Priority,
          field0Values: preferred.field0Values,
          field1Name: preferred.field1Name,
          field1Type: preferred.field1Type,
          field1Priority: preferred.field1Priority,
          field2Name: preferred.field2Name,
          field2Type: preferred.field2Type,
          field2Priority: preferred.field2Priority,
          field3Name: preferred.field3Name,
          field3Type: preferred.field3Type,
          field3Priority: preferred.field3Priority,
          field4Name: preferred.field4Name,
          field4Type: preferred.field4Type,
          field4Priority: preferred.field4Priority,
          field5Type: preferred.field5Type,
          field5Priority: preferred.field5Priority,
          field6Type: preferred.field6Type,
          field6Priority: preferred.field6Priority,
          field7Type: preferred.field7Type,
          field7Priority: preferred.field7Priority,
          field8Type: preferred.field8Type,
          field8Priority: preferred.field8Priority,
          field8Values: preferred.field8Values,
          field9Type: preferred.field9Type,
          field9Priority: preferred.field9Priority,
        });
      } catch {
        // Keep the component usable even if journal defaults fail.
      }
    };

    loadDefaultJournal();
  }, [financeService, treeService, store]);

  useEffect(() => {
    setShowAddEditRow(false);
    (async () => {
      setShowLoader(true);
      await loadStockChangeLineData();
      setShowLoader(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, reloadComponent]);

  const updateStockChangeLineData = (stockChangeLineData: any[]) => {
    if (!stockChangeLineData) return [];
    return stockChangeLineData
      .filter((item) => item.itemName && item.itemName.trim() !== "")
      .map((item) => ({
        ...item,
      }));
  };

  // Function to load stock change line data
  const loadStockChangeLineData = async () => {
    // Call API to fetch transaction data
    let transactionResp: any = await invoiceService.listTransaction(
      new ListTransactionRequest(store.Server, store.SessionId, {
        limit: 50,
        offset: 0,
        activity: String(selectedItem?.id),
      }),
    );

    // If API call is successful
    if (transactionResp?.result === "OK") {
      // Map the transaction data to desired format
      const qty = (item: any) => {
        const raw =
          typeof item.itemQuantity === "string" ? item.itemQuantity : null;
        return raw
          ? formatDecimalToString(Number.parseFloat(raw).toFixed(2))
          : "0";
      };
      //filter where itemName is empty
      let stockChangeLineData = transactionResp?.TRANSACTION?.map(
        (item: any) => {
          const formattedDate = item.transactionDate
            ? item.transactionDate.substring(0, 10)
            : "";
          return {
            id: item.id,
            itemQuantity: qty(item),
            itemName: item.itemName || "",
            bookkeepingAmount: item.bookkeepingAmount || "",
            bookkeepingAmountWithCurr: item.bookkeepingAmount
              ? formatDecimalToString(
                  Number.parseFloat(item.bookkeepingAmount).toFixed(2),
                ) +
                " " +
                (item.bookkeepingCurrency || "CZK")
              : "",
            transactionDate: formattedDate,
            createdByName: item.createdByName || "",
            journalName: item.journalName || "",
            entry: item.entry,
            attrib0: item.attrib0 || "",
            attrib0_id: item.attrib0_id || "",
            attrib1: item.attrib1 || "",
            attrib1_id: item.attrib1_id || "",
            attrib2: item.attrib2 || "",
            attrib2_id: item.attrib2_id || "",
            attrib3: item.attrib3 || "",
            attrib3_id: item.attrib3_id || "",
            attrib4: item.attrib4 || "",
            attrib4_id: item.attrib4_id || "",
          };
        },
      );

      setRowsData(updateStockChangeLineData(stockChangeLineData));
    }
  };

  const shouldUseModalLayout = () => {
    return deviceInfo.shouldUseMobileLayout() || deviceInfo.screenWidth <= 1440;
  };

  const openAddEditDialog = () => {
    shouldUseModalLayout() ? setIsOpen(true) : setShowAddEditRow(true);
  };

  const handleRowClose = (): void => {
    setSelectedRow(defaultRowData);
    setShowAddEditRow(false);
    setViewMode(false);
    setSelectedKeys(new Set());
    shouldUseModalLayout() && setIsOpen(false);
  };

  const handleRowClick = (keys: any): void => {
    const selectedKey =
      keys === "all" ? null : Array.from(keys as Set<string>)[0];
    if (!selectedKey) {
      handleRowClose();
      return;
    }
    const row = rowsData.find((r: any) => String(r.id) === String(selectedKey));
    if (!row) return;
    setSelectedKeys(new Set([String(selectedKey)]));
    setSelectedRow({
      id: row.id,
      itemQuantity: parseFloat(row.itemQuantity) || 1,
      finance: row.bookkeepingAmount || "0",
      itemName: row.itemName || "",
      itemRefId: "",
      transactionDate: row.transactionDate || "",
      attrib0: row.attrib0 || "",
      attrib0_id: row.attrib0_id || "",
      attrib1: row.attrib1 || "",
      attrib1_id: row.attrib1_id || "",
      attrib2: row.attrib2 || "",
      attrib2_id: row.attrib2_id || "",
      attrib3: row.attrib3 || "",
      attrib3_id: row.attrib3_id || "",
      attrib4: row.attrib4 || "",
      attrib4_id: row.attrib4_id || "",
    });
    setViewMode(true);
    shouldUseModalLayout() ? setIsOpen(true) : setShowAddEditRow(true);
  };

  const addRecord = async (record: any) => {
    try {
      setShowLoader(true);

      const activityId = String(selectedItem?.id);
      const projectId = selectedItem?.parent_id
        ? String(selectedItem.parent_id)
        : undefined;
      const folderId = selectedItem?.parent?.parent_id
        ? String(selectedItem.parent.parent_id)
        : undefined;
      const amount = String(record.finance ?? 0);
      const amountNeg = String(-Number(record.finance ?? 0));
      const quantity = String(record.itemQuantity ?? 1);
      const txDate = record.transactionDate
        ? `${String(record.transactionDate).substring(0, 10)}T00:00:00`
        : `${new Date().toISOString().split("T")[0]}T00:00:00`;
      const sharedFields = {
        treeHandle: defaultJournal?.treeHandle,
        templateAcc: defaultJournal?.templateAcc,
        journal: defaultJournal?.id,
        journalName: defaultJournal?.name,
        activity: activityId,
        project: projectId,
        folder: folderId,
        transactionDate: txDate,
        bookkeepingCurrency: "CZK",
        bookkeepingCurrencyRate: "1",
        bookkeepingUnitPrice: "0",
        catalogPrice: "0",
        otherAmount: "0",
        otherCurrencyRate: "1",
        otherUnitPrice: "0",
        invoicedCat: "0",
        invoicedQty: "0",
        orderIndex: "0",
        splitSwitch: "2",
        type: "0",
        VAT: "none",
        VATValue: 0,
      };

      const result = await financeService.createEntry(
        new CreateEntryRequest(store.Server, store.SessionId, {
          journal: defaultJournal?.id,
          treeHandle: defaultJournal?.treeHandle,
          templateAcc: defaultJournal?.templateAcc,
          subject: folderId,
          plan: "0",
          activity: activityId,
          TRANSACTION: [
            {
              // Debit leg — stock in (accountBase, positive amount, carries the item ref)
              ...sharedFields,
              account: defaultJournal?.accountBase,
              accountName: defaultJournal?.accountBaseName,
              item: record.partID,
              itemName: record.itemName,
              itemQuantity: quantity,
              bookkeepingAmount: amount,
              bookkeepingAmountReverted: amountNeg,
              description: "Stock transaction",
              VATTitle: "G",
              attrib0: record.attrib0 || undefined,
              attrib0_id: record.attrib0_id || undefined,
              attrib1: record.attrib1 || undefined,
              attrib1_id: record.attrib1_id || undefined,
              attrib2: record.attrib2 || undefined,
              attrib2_id: record.attrib2_id || undefined,
              attrib3: record.attrib3 || undefined,
              attrib3_id: record.attrib3_id || undefined,
              attrib4: record.attrib4 || undefined,
              attrib4_id: record.attrib4_id || undefined,
            },
            {
              // Credit leg — offset entry (accountTotal, negative amount)
              ...sharedFields,
              account: defaultJournal?.accountTotal,
              accountName: defaultJournal?.accountTotalName,
              itemQuantity: "1",
              bookkeepingAmount: amountNeg,
              bookkeepingAmountReverted: amount,
              description: "Reverse to stock transaction",
            },
          ],
        }),
      );

      // console.log('result', result);
      if (result.result === "OK") {
        await loadStockChangeLineData();
        handleRowClose();
        ToastQueue.positive("Record saved successfully", { timeout: 3000 });
      } else {
        ToastQueue.negative(result.EXCEPTION.reason, { timeout: 3000 });
      }
    } catch (error: any) {
      ToastQueue.negative(error.message, { timeout: 3000 });
      throw error;
    } finally {
      setShowLoader(false);
    }
  };

  const updateRecord = async (record: any) => {
    // Edit = delete the existing entry then recreate it (no UpdateEntry API exists).
    if (record.entry) {
      try {
        setShowLoader(true);
        const deleteResult = await financeService.deleteEntry(
          new DeleteEntryRequest(store.Server, store.SessionId, record.entry),
        );
        if (deleteResult.result !== "OK") {
          ToastQueue.negative(deleteResult.EXCEPTION?.reason, {
            timeout: 3000,
          });
          return;
        }
      } catch (error: any) {
        ToastQueue.negative(error.message, { timeout: 3000 });
        return;
      } finally {
        setShowLoader(false);
      }
    }
    await addRecord(record);
  };

  const deleteRecord = async (record: any) => {
    try {
      setShowLoader(true);

      let result = await financeService.deleteEntry(
        new DeleteEntryRequest(
          store.Server,
          store.SessionId,
          record.selectedRow.entry,
        ),
      );

      // console.log('result', result);
      if (result.result === "OK") {
        await loadStockChangeLineData();
        handleRowClose();
        ToastQueue.positive("Record deleted successfully", { timeout: 3000 });
      } else {
        ToastQueue.negative(result.EXCEPTION.reason, { timeout: 3000 });
      }
    } catch (error) {
    } finally {
      setShowLoader(false);
    }
  };

  const StockChangeDialog = ({
    close,
    selectedRow,
  }: {
    close: () => void;
    selectedRow: any;
  }) => (
    <Dialog size="S">
      <Flex UNSAFE_className={styles.delete_pop_up_parent}>
        <Content UNSAFE_className={styles.delete_pop_up_header}>
          {t("remove_stock", { ns: "layout_components" })}
        </Content>
        <View UNSAFE_className={styles.delete_pop_up_line}></View>
        <View UNSAFE_className={styles.delete_pop_up_warning_text}>
          {t("remove_stock_message", { ns: "layout_components" })}
        </View>
        <View UNSAFE_className={styles.buttons_parent}>
          <View UNSAFE_className={styles.button_cancel}>
            <Link
              UNSAFE_className={styles.delete_pop_up_cancel_text}
              isQuiet
              onPress={close}
            >
              {t("no_keep_it", { ns: "layout_components" })}
            </Link>
          </View>
          <View UNSAFE_className={styles.button_confirm}>
            <div className={styles.trash_icon}>
              <TrashIcon size={32} />
            </div>
            <Link
              UNSAFE_className={styles.delete_pop_up_confirm_text}
              isQuiet
              onPress={() => deleteRecord(selectedRow)}
            >
              {t("yes_remove", { ns: "layout_components" })}
            </Link>
          </View>
        </View>
        <View UNSAFE_className={styles.ellipse_div}>
          <View UNSAFE_className={styles.delete_alert_icon}>
            <AlertIconFill size={72} />
          </View>
        </View>
      </Flex>
    </Dialog>
  );

  const RemoveStockChangeButton = (selectedRow: any) =>
    Number(selectedRow.id) !== 0 && (
      <Flex marginTop={isMobile ? "10px" : "0"}>
        <DialogTrigger isDismissable type="modal">
          <ActionButton UNSAFE_style={{ border: "none" }}>
            <TrashIcon size={32} />
          </ActionButton>
          {(close) => (
            <StockChangeDialog close={close} selectedRow={selectedRow} />
          )}
        </DialogTrigger>
      </Flex>
    );

  const closeDialog = () => {
    setIsOpen(false);
  };

  if (showLoader) {
    return (
      <Flex width="100%" justifyContent={"center"} marginTop={10}>
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  } else {
    return (
      <View position={"relative"} width={"100%"}>
        {/* Table section */}
        <View position={"relative"} width={"100%"}>
          <Flex direction={"column"} gap={"size-150"} width={"100%"}>
            <Flex
              direction={"row"}
              alignItems={"start"}
              justifyContent={"start"}
              UNSAFE_className={"heading_text_parent"}
            >
              <Content
                position={"relative"}
                UNSAFE_className={styles.heading_text}
              >
                {t("stock_movement", { ns: "layout_components" })}
              </Content>
              <Flex
                direction={"row"}
                alignItems={"center"}
                justifyContent={"center"}
                gap={"size-200"}
                UNSAFE_className={styles.icon_add_parent}
              >
                <Content>
                  <Link
                    isQuiet
                    onPress={(e) => {
                      setSelectedRow(defaultRowData);
                      setViewMode(false);
                      setSelectedKeys(new Set());
                      openAddEditDialog();
                    }}
                  >
                    <i className="bi bi-plus fs-5">
                      <View UNSAFE_className={styles.icon_add_text}>
                        {t("add_new_stock_receipt", {
                          ns: "layout_components",
                        })}
                      </View>
                    </i>
                  </Link>
                </Content>
              </Flex>
            </Flex>
            <Flex
              direction={"column"}
              width={"100%"}
              UNSAFE_style={tableStyles}
            >
              <Flex height={containerHeight}>
                <TableView
                  UNSAFE_className="tblLayoutComponent"
                  aria-label="Stock Change Line"
                  onSelectionChange={handleRowClick}
                  selectionMode="single"
                  selectedKeys={selectedKeys}
                  selectionStyle="highlight"
                  width={"100%"}
                  height={tableHeight}
                  UNSAFE_style={{ position: "absolute" }}
                >
                  <TableHeader
                    columns={
                      deviceInfo.shouldUseMobileLayout()
                        ? columnsDataMobile
                        : columnsData
                    }
                  >
                    {(column) => (
                      <Column
                        showDivider
                        key={column.key}
                        width={column?.width}
                      >
                        {column.name}
                      </Column>
                    )}
                  </TableHeader>
                  <TableBody items={rowsData}>
                    {(item: any) => (
                      <Row>
                        {(columnKey) => {
                          if (columnKey === "id") {
                            return (
                              <Cell>
                                <Flex
                                  direction="row"
                                  gap="size-50"
                                  alignItems="center"
                                >
                                  <RemoveStockChangeButton selectedRow={item} />
                                </Flex>
                              </Cell>
                            );
                          }
                          return (
                            <Cell>{(item as any)[String(columnKey)]}</Cell>
                          );
                        }}
                      </Row>
                    )}
                  </TableBody>
                </TableView>
              </Flex>
            </Flex>

            {/* Mobile Dialog Container inside absolute positioned area */}
            {shouldUseModalLayout() && (
              <DialogContainer
                isDismissable
                onDismiss={() => {
                  setIsOpen(false);
                }}
              >
                {isOpen && (
                  <Dialog id="dlgStockChangeLine">
                    <Heading>
                      {selectedRow.id === "0"
                        ? t("add", { ns: "layout_components" })
                        : t("edit", { ns: "layout_components" })}{" "}
                      {t("product", { ns: "layout_components" })}
                    </Heading>
                    <Content>
                      <StockChangeLineAddEdit
                        id={String(selectedItem?.id)}
                        defaultRowData={defaultRowData}
                        defaultJournal={defaultJournal}
                        selectedRowData={selectedRow}
                        handleRowClose={handleRowClose}
                        addRecord={addRecord}
                        updateRecord={updateRecord}
                        deleteRecord={deleteRecord}
                        closeDialog={closeDialog}
                        activeAttributesData={activeAttributesData}
                        viewMode={viewMode}
                        styles={styles}
                      />
                    </Content>
                  </Dialog>
                )}
              </DialogContainer>
            )}
          </Flex>
        </View>

        {/* StockChangeLineAddEdit section - OUTSIDE absolute positioning for proper document flow */}
        {!shouldUseModalLayout() && showAddEditRow && (
          <Flex
            direction={"column"}
            width={"100%"}
            marginTop={"size-200"}
            UNSAFE_style={{ zIndex: 2 }}
          >
            <StockChangeLineAddEdit
              id={String(selectedItem?.id)}
              defaultRowData={defaultRowData}
              defaultJournal={defaultJournal}
              selectedRowData={selectedRow}
              handleRowClose={handleRowClose}
              addRecord={addRecord}
              updateRecord={updateRecord}
              deleteRecord={deleteRecord}
              closeDialog={closeDialog}
              activeAttributesData={activeAttributesData}
              viewMode={viewMode}
              styles={styles}
            />
          </Flex>
        )}
      </View>
    );
  }
}

export const StockChangeLineComponent = LayoutComponent(StockChangeLine);
